/* Local, illustrative product views. Never used as print data or a checkout prerequisite. */
(() => {
  'use strict';
  const placements = Object.freeze({
    'tshirt-white': { x: .5, y: .32, width: .32, height: .32 * 136.8 / 237 },
    'tshirt-black': { x: .5, y: .32, width: .32, height: .32 * 136.8 / 237 },
    'hoodie-white': { x: .5, y: .37, width: .29, height: .29 * 135.5 / 235 },
    'hoodie-black': { x: .5, y: .37, width: .29, height: .29 * 135.5 / 235 },
    ceramic: { x: .5, y: .55, width: .45, preserveAspect: true },
    enamel: { x: .475, y: .56, width: .45, preserveAspect: true },
    'beer-mug': { x: .533, y: .56, width: .46, preserveAspect: true }
  });
  // These three frontal placements are independent of the six established views.
  // Photo print-area coordinates are normalized to the square product image.
  const additionalPlacements = Object.freeze({
    stickers: { x: .5, y: .5, width: .8 },
    flag: { x: .501, y: .501, width: .858, photoScaleY: .940 },
    beerdeckel: { x: .5, y: .4985, width: .915, photoScaleY: .9566 }
  });
  const RECTANGULAR_FLAG_PLACEMENT = Object.freeze({ ...additionalPlacements.flag, width: .904 });
  const photos = new Map();
  function loadImage(source) {
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.crossOrigin = 'anonymous';
      img.decoding = 'async';
      img.onload = () => resolve(img);
      img.onerror = () => reject(new Error('product-image-unavailable'));
      img.src = source;
    });
  }
  function getPhoto(source) {
    if (!photos.has(source)) {
      const pending = loadImage(source).catch(error => { photos.delete(source); throw error; });
      photos.set(source, pending);
    }
    return photos.get(source);
  }
  function drawArtwork(context, artwork, placement, size) {
    const layer = document.createElement('canvas');
    layer.width = layer.height = size;
    const art = layer.getContext('2d');
    if (!art) throw new Error('product-canvas-unavailable');
    const width = size * placement.width;
    const sourceWidth = artwork.naturalWidth || artwork.width;
    const sourceHeight = artwork.naturalHeight || artwork.height;
    const height = placement.preserveAspect ? width * sourceHeight / sourceWidth : size * placement.height;
    const left = size * placement.x - width / 2;
    const top = size * placement.y - height / 2;
    // Frontal views keep the complete motif intact; shading never reshapes the oval.
    art.drawImage(artwork, 0, 0, sourceWidth, sourceHeight, left, top, width, height);
    // A subtle light falloff remains on the motif, while white ink stays white on dark fabric.
    art.globalCompositeOperation = 'source-atop';
    const shade = art.createLinearGradient(left, 0, left + width, 0);
    shade.addColorStop(0, 'rgba(0,0,0,.12)');
    shade.addColorStop(.4, 'rgba(0,0,0,0)');
    shade.addColorStop(1, 'rgba(0,0,0,.10)');
    art.fillStyle = shade;
    art.fillRect(left - 1, top, width + 2, height + size * .02);
    context.drawImage(layer, 0, 0);
    layer.width = layer.height = 1;
  }
  function drawAdditionalBase(context, base, kind, size) {
    // The generated frontal photos were slightly too tall. Correct only these
    // new blank surfaces to the physical product aspect; never reshape artwork.
    const height = size * additionalPlacements[kind].photoScaleY;
    context.save();
    context.fillStyle = '#ffffff';
    context.fillRect(0, 0, size, size);
    context.drawImage(base, 0, (size - height) / 2, size, height);
    context.restore();
  }
  function makeAdditionalPrint(artwork, source, kind) {
    const width = artwork.naturalWidth || artwork.width;
    const height = artwork.naturalHeight || artwork.height;
    if (!(width > 0 && height > 0)) throw new Error('product-artwork-unavailable');
    if (kind === 'stickers') return artwork;
    const print = document.createElement('canvas');
    // UI-only replicas of the existing print layouts; no PDF/export is called.
    print.width = kind === 'flag' ? 1004 : 136 * 8;
    print.height = kind === 'flag' ? 584 : 101 * 8;
    const context = print.getContext('2d');
    if (!context) throw new Error('product-canvas-unavailable');
    if (kind === 'flag') {
      // Oval designs keep white corners; rectangular fabric carries the motif color to the edge.
      context.fillStyle = source?.shape === 'rect' && typeof source.shapeBackgroundColor === 'string'
        && source.shapeBackgroundColor.trim() ? source.shapeBackgroundColor : '#ffffff';
      context.fillRect(0, 0, print.width, print.height);
      context.drawImage(artwork, 0, 0, width, height, 0, 0, print.width, print.height);
    } else if (kind === 'beerdeckel') {
      // The 136 x 101 mm oval surrounds the centered 136 x 74.5 mm artwork box.
      context.save();
      context.beginPath();
      context.ellipse(print.width / 2, print.height / 2, print.width / 2, print.height / 2, 0, 0, Math.PI * 2);
      context.clip();
      context.fillStyle = typeof source?.backgroundColor === 'string' && source.backgroundColor.trim()
        ? source.backgroundColor : '#ffffff';
      context.fillRect(0, 0, print.width, print.height);
      context.drawImage(artwork, 0, 0, width, height, 0, 13.25 * 8, 136 * 8, 74.5 * 8);
      context.restore();
    }
    return print;
  }
  function drawAdditionalArtwork(context, artwork, source, kind, size) {
    const rectangularFlag = kind === 'flag' && source?.shape === 'rect';
    const placement = rectangularFlag ? RECTANGULAR_FLAG_PLACEMENT : additionalPlacements[kind];
    const print = makeAdditionalPrint(artwork, source, kind);
    const sourceWidth = print.naturalWidth || print.width;
    const sourceHeight = print.naturalHeight || print.height;
    const width = size * placement.width;
    const height = width * sourceHeight / sourceWidth;
    const left = size * placement.x - width / 2;
    const top = size * placement.y - height / 2;
    context.save();
    if (kind === 'stickers') {
      // Canvas shadows follow the PNG alpha: no contour or black sticker border.
      context.shadowColor = 'rgba(41, 34, 21, .16)';
      context.shadowBlur = size * .018;
      context.shadowOffsetY = size * .008;
    } else if (!rectangularFlag) {
      // These new bases are white paper/fabric, so their material light stays visible.
      context.globalCompositeOperation = 'multiply';
    } else {
      // Cover the entire rectangular flag, including the hem, without tint from the base photo.
      context.globalCompositeOperation = 'source-over';
    }
    context.drawImage(print, 0, 0, sourceWidth, sourceHeight, left, top, width, height);
    context.restore();
    if (print !== artwork) print.width = print.height = 1;
  }
  // Persist a small illustrative product image with each selected cart line.
  // The caller applies its own short deadline; this optional renderer never
  // writes to visible editor canvases, design IDs, print exports or cart state.
  function cartPreviewKindForKey(key) {
    const apparel = /^(tshirt|hoodie)_(white|black)_(s|m|l|xl|xxl|xxxl)$/.exec(key);
    if (apparel && (apparel[1] === 'tshirt' || apparel[3] !== 'xxxl')) return `${apparel[1]}-${apparel[2]}`;
    if (key === 'mug_ceramic') return 'ceramic';
    if (key === 'mug_enamel') return 'enamel';
    if (key === 'beer_mug') return 'beer-mug';
    if (/^flag_(100x58|150x86|200x115|300x173)$/.test(key)) return 'flag';
    if (/^beerdeckel_(1|5|10|25|50|100|250|500|1000|1500|2500|5000)$/.test(key)) return 'beerdeckel';
    // Stickers already have their accurate flat design thumbnail.
    return '';
  }
  function compactCartPreview(output) {
    let smaller = null;
    try {
      for (const edge of [400, 320, 280]) {
        let candidate = output;
        if (edge !== 400) {
          if (!smaller) smaller = document.createElement('canvas');
          smaller.width = smaller.height = edge;
          const context = smaller.getContext('2d', { alpha: false });
          if (!context) continue;
          context.fillStyle = '#ffffff';
          context.fillRect(0, 0, edge, edge);
          context.drawImage(output, 0, 0, edge, edge);
          candidate = smaller;
        }
        for (const quality of [.78, .65, .5]) {
          const value = candidate.toDataURL('image/jpeg', quality);
          if (typeof value === 'string' && value.startsWith('data:image/jpeg;base64,')
            && value.length > 23 && value.length <= 32000) return value;
        }
      }
    } finally {
      if (smaller) smaller.width = smaller.height = 1;
    }
    return '';
  }
  async function renderCartProductPreview(artwork, source, kind) {
    let output = null;
    try {
      const photo = document.querySelector(`[data-product-preview="${kind}"] [data-product-preview-base]`);
      const photoSource = photo?.currentSrc || photo?.src;
      if (!photoSource) return '';
      const base = await getPhoto(photoSource);
      output = document.createElement('canvas');
      output.width = output.height = 400;
      const context = output.getContext('2d', { alpha: false });
      if (!context) return '';
      context.fillStyle = '#ffffff';
      context.fillRect(0, 0, 400, 400);
      if (additionalPlacements[kind]) {
        drawAdditionalBase(context, base, kind, 400);
        drawAdditionalArtwork(context, artwork, source, kind, 400);
      } else {
        context.drawImage(base, 0, 0, 400, 400);
        drawArtwork(context, artwork, placements[kind], 400);
      }
      return compactCartPreview(output);
    } catch (_) {
      return '';
    } finally {
      if (output) output.width = output.height = 1;
    }
  }
  window.NH_getCartProductPreviews = async function NH_getCartProductPreviews(selection) {
    try {
      if (!Array.isArray(selection) || typeof window.NH_getProductArtwork !== 'function') return {};
      const requested = new Map();
      for (const item of selection) {
        const key = typeof item === 'string' ? item : item?.key;
        if (typeof key !== 'string' || (typeof item === 'object' && item !== null
          && item.quantity !== undefined && !(Number(item.quantity) > 0))) continue;
        const kind = cartPreviewKindForKey(key);
        if (kind) requested.set(key, kind);
      }
      if (!requested.size) return {};
      const current = await window.NH_getProductArtwork();
      if (!current?.dataUrl || typeof current.fingerprint !== 'string' || !current.fingerprint) return {};
      const source = Object.freeze({ dataUrl: current.dataUrl, fingerprint: current.fingerprint,
        shape: current.shape, backgroundColor: current.backgroundColor, shapeBackgroundColor: current.shapeBackgroundColor });
      const artwork = await loadImage(source.dataUrl);
      const completed = new Map(await Promise.all(Array.from(new Set(requested.values()), async kind =>
        [kind, await renderCartProductPreview(artwork, source, kind)])));
      // A delayed image load must never attach a prior motif to the new order.
      const latest = await window.NH_getProductArtwork();
      if (!latest || latest.fingerprint !== source.fingerprint) return {};
      const result = {};
      requested.forEach((kind, key) => {
        const preview = completed.get(kind);
        if (preview) result[key] = preview;
      });
      return result;
    } catch (_) {
      return {};
    }
  };
  function init() {
    const modal = document.getElementById('export-modal');
    if (!modal) return;
    const views = Array.from(modal.querySelectorAll('[data-product-preview]'));
    if (!views.length) return;
    let request = 0;
    let scheduled = false;
    const isOpen = () => modal.getAttribute('aria-hidden') === 'false';
    const isVisible = view => !view.closest('[data-body]')?.hidden;
    function reset(view) {
      view.dataset.previewState = 'loading';
      const output = view.querySelector('canvas');
      if (output) output.hidden = true;
      const status = view.querySelector('[data-product-preview-status]');
      if (status) status.textContent = 'Dein Motiv wird eingesetzt …';
    }
    async function update() {
      const generation = ++request;
      if (!isOpen()) return;
      const visible = views.filter(isVisible);
      if (!visible.length) return;
      visible.forEach(reset);
      let source = null;
      try { source = await window.NH_getProductArtwork?.(); } catch (_) { /* optional preview */ }
      if (generation !== request || !isOpen()) return;
      let artwork = null;
      try { if (source?.dataUrl) artwork = await loadImage(source.dataUrl); } catch (_) { /* photo fallback */ }
      if (generation !== request || !isOpen()) return;
      await Promise.all(visible.map(async view => {
        const current = () => generation === request && isOpen() && isVisible(view);
        const status = view.querySelector('[data-product-preview-status]');
        if (!artwork) {
          if (current()) {
            view.dataset.previewState = 'unavailable';
            if (status) status.textContent = 'Motivvorschau gerade nicht verfügbar. Deine Gestaltung bleibt erhalten.';
          }
          return;
        }
        try {
          const kind = view.dataset.productPreview;
          const photo = view.querySelector('[data-product-preview-base]');
          const base = kind === 'stickers' ? null : await getPhoto(photo.currentSrc || photo.src);
          if (!current()) return;
          const output = view.querySelector('canvas');
          const context = output?.getContext('2d');
          if (!context) throw new Error('product-canvas-unavailable');
          output.width = output.height = 800;
          if (base) {
            if (additionalPlacements[kind]) drawAdditionalBase(context, base, kind, 800);
            else context.drawImage(base, 0, 0, 800, 800);
          }
          if (additionalPlacements[kind]) drawAdditionalArtwork(context, artwork, source, kind, 800);
          else drawArtwork(context, artwork, placements[kind], 800);
          // Make the finished canvas visible in one paint (also on Safari).
          output.hidden = false;
          view.dataset.previewState = 'ready';
          if (status) status.textContent = 'Dein Design auf diesem Produkt';
        } catch (_) {
          if (current()) {
            view.dataset.previewState = 'unavailable';
            if (status) status.textContent = 'Motivvorschau gerade nicht verfügbar. Deine Gestaltung bleibt erhalten.';
          }
        }
      }));
    }
    function schedule() {
      if (scheduled) return;
      scheduled = true;
      requestAnimationFrame(() => { scheduled = false; void update(); });
    }
    const observer = new MutationObserver(records => {
      if (records.some(record => (record.target === modal && record.attributeName === 'aria-hidden')
        || (record.target.hasAttribute('data-body') && record.attributeName === 'hidden'))) schedule();
    });
    observer.observe(modal, { attributes: true, attributeFilter: ['aria-hidden', 'hidden'], subtree: true });
    // Undo/redo shortcuts can still change the design while the product dialog is open.
    window.historyService?.onChange?.(() => { if (isOpen()) schedule(); });
    schedule();
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init, { once: true });
  else init();
})();
